import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';
import { api, ApiError } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { BackLink, PageHeader } from '@/layouts/AppLayout';

interface UserDetail {
  id: string;
  email: string;
  fullName: string;
  role: string;
  status: string;
  lastLoginAt: string | null;
  createdAt: string;
}

const profileSchema = z.object({
  fullName: z.string().min(2, 'At least 2 characters'),
  email: z.string().email('Valid email required'),
  role: z.enum(['super_admin', 'admin']),
  status: z.enum(['active', 'disabled']),
});
type ProfileValues = z.infer<typeof profileSchema>;

const pwSchema = z.object({
  password: z
    .string()
    .min(10, 'At least 10 characters')
    .regex(/^(?=.*[A-Za-z])(?=.*\d)/, 'Must include a letter and a digit'),
});
type PwValues = z.infer<typeof pwSchema>;

export function UserProfilePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: user, isLoading } = useQuery({
    queryKey: ['users', id],
    queryFn: () => api.get<UserDetail>(`/users/${id}`),
    enabled: !!id,
  });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    setError,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<ProfileValues>({
    resolver: zodResolver(profileSchema),
    values: user
      ? { fullName: user.fullName, email: user.email, role: user.role as ProfileValues['role'], status: user.status as ProfileValues['status'] }
      : undefined,
  });

  const pwForm = useForm<PwValues>({ resolver: zodResolver(pwSchema) });

  const updateMutation = useMutation({
    mutationFn: (data: ProfileValues) => api.patch(`/users/${id}`, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['users'] });
      toast.success('User updated');
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : 'Failed to update user';
      setError('root', { message: msg });
    },
  });

  const pwMutation = useMutation({
    mutationFn: (data: PwValues) => api.post(`/users/${id}/reset-password`, data),
    onSuccess: () => {
      pwForm.reset();
      toast.success('Password updated');
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : 'Failed to update password';
      pwForm.setError('root', { message: msg });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/users/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['users'] });
      toast.success('User deleted');
      navigate('/settings/users');
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : 'Failed to delete user';
      toast.error(msg);
    },
  });

  const handleDelete = () => {
    if (!confirm(`Delete ${user?.fullName}? This cannot be undone.`)) return;
    deleteMutation.mutate();
  };

  const role = watch('role');
  const status = watch('status');

  if (isLoading) return <div className="flex justify-center py-16"><Spinner className="h-6 w-6" /></div>;
  if (!user) return <p className="p-6 text-danger-700">User not found.</p>;

  return (
    <div>
      <PageHeader title={user.fullName} description={user.email} />
      <div className="p-6 space-y-6 max-w-lg">
        <BackLink to="/settings/users" label="Back to users" />

        {/* Meta */}
        <div className="flex items-center gap-3 text-sm text-neutral-500">
          <Badge variant={user.role as 'super_admin' | 'admin'}>
            {user.role === 'super_admin' ? 'Super Admin' : 'Admin'}
          </Badge>
          <Badge variant={user.status as 'active' | 'disabled'}>{user.status}</Badge>
          <span>Last login: {formatDate(user.lastLoginAt)}</span>
        </div>

        {/* Profile form */}
        <Card>
          <CardHeader><CardTitle>Profile</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit((d) => updateMutation.mutate(d))} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="fullName">Full name</Label>
                <Input id="fullName" {...register('fullName')} />
                {errors.fullName && <p className="text-xs text-danger-700">{errors.fullName.message}</p>}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" {...register('email')} />
                {errors.email && <p className="text-xs text-danger-700">{errors.email.message}</p>}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Role</Label>
                  <Select value={role} onValueChange={(v) => setValue('role', v as ProfileValues['role'], { shouldDirty: true })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="super_admin">Super Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Status</Label>
                  <Select value={status} onValueChange={(v) => setValue('status', v as ProfileValues['status'], { shouldDirty: true })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="disabled">Disabled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {errors.root && (
                <p className="rounded-md bg-danger-50 px-3 py-2 text-sm text-danger-700">{errors.root.message}</p>
              )}
              <Button type="submit" disabled={isSubmitting || !isDirty}>
                {isSubmitting ? 'Saving…' : 'Save changes'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Reset password */}
        <Card>
          <CardHeader><CardTitle>Reset password</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={pwForm.handleSubmit((d) => pwMutation.mutate(d))} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="newPassword">New password</Label>
                <Input id="newPassword" type="password" placeholder="Min 10 chars, letter + digit" {...pwForm.register('password')} />
                {pwForm.formState.errors.password && (
                  <p className="text-xs text-danger-700">{pwForm.formState.errors.password.message}</p>
                )}
              </div>
              {pwForm.formState.errors.root && (
                <p className="rounded-md bg-danger-50 px-3 py-2 text-sm text-danger-700">{pwForm.formState.errors.root.message}</p>
              )}
              <Button type="submit" variant="outline" disabled={pwForm.formState.isSubmitting}>
                {pwForm.formState.isSubmitting ? 'Updating…' : 'Update password'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Danger zone */}
        <Card className="border-danger-700/20">
          <CardHeader><CardTitle className="text-danger-700">Danger zone</CardTitle></CardHeader>
          <CardContent>
            <p className="mb-3 text-sm text-neutral-500">
              Permanently delete this user. All their data will remain in the audit log.
            </p>
            <Button variant="destructive" onClick={handleDelete} disabled={deleteMutation.isPending}>
              {deleteMutation.isPending ? 'Deleting…' : 'Delete user'}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
